import type { ReactNode, Ref } from 'react'

import { Select } from '@gto/ui/select'

import type { FieldShellProps } from './field-shell'
import { FieldShell } from './field-shell'

export type SelectFieldOption = {
  value: string
  label: ReactNode
  /** Renders the option greyed and unselectable (MUI MenuItem `disabled` parity). */
  disabled?: boolean
}

type SelectFieldProps = FieldShellProps & {
  value: string
  onValueChange: (value: string) => void
  /** Callers keep the '' "all"-sentinel contract: pass it as the first option. */
  options: SelectFieldOption[]
  /** Accessible name for compact, label-free controls such as toolbar filters. */
  ariaLabel?: string
  size?: 'small' | 'medium'
  name?: string
  /** react-hook-form field.onBlur target (touched tracking parity with the text fields). */
  onBlur?: () => void
  /** react-hook-form field.ref target, for focus-on-error. */
  triggerRef?: Ref<HTMLButtonElement>
}

/** DS replacement for MUI's select-mode TextField (`<TextField select>` + MenuItems). */
export const SelectField = ({
  label,
  error,
  helperText,
  disabled,
  required,
  className,
  value,
  onValueChange,
  options,
  ariaLabel,
  size = 'small',
  name,
  onBlur,
  triggerRef,
}: SelectFieldProps) => (
  <FieldShell
    className={className}
    disabled={disabled}
    error={error}
    helperText={helperText}
    label={label}
    required={required}
    control={
      <Select
        disabled={disabled}
        name={name}
        value={value}
        onValueChange={onValueChange}
      >
        <Select.Trigger
          ref={triggerRef}
          aria-label={ariaLabel}
          aria-required={required === true ? true : undefined}
          size={size}
          onBlur={onBlur}
        >
          <Select.Value>
            {(selected: string) => options.find((option) => option.value === selected)?.label}
          </Select.Value>
          <Select.Icon />
        </Select.Trigger>
        <Select.Dropdown>
          <Select.List>
            {options.map((option) => (
              <Select.Item
                key={option.value}
                disabled={option.disabled}
                value={option.value}
              >
                {option.label}
              </Select.Item>
            ))}
          </Select.List>
        </Select.Dropdown>
      </Select>
    }
  />
)
